import React, { useEffect } from "react";
import ReactDOM from "react-dom/client";
import App from "./App";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { Provider, useDispatch } from "react-redux";
import { ThemeProvider } from "@mui/material/styles";
import { CssBaseline } from "@mui/material";
import { HelmetProvider } from "react-helmet-async";
import LoginPage from "./pages/LoginPage";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { PublicClientApplication, EventType } from "@azure/msal-browser";
import { msalConfig } from "./config/msAuthConfig";
import { MsalProvider } from "@azure/msal-react";
import { deinitializeGA, initializeGA, trackPageView } from "./analytics";
import HomePage from "./pages/HomePage";
import Bookmarks from "./pages/Bookmarks";
import LegalLibrary from "./components/LegalLibrary";
import SignUpForm from "./pages/SignUpForm";
import NadraOnboardingForm from "./pages/NadraOnboardingForm";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import { PersistGate } from "redux-persist/integration/react";
import { persistor, store } from "./store";
import ApplicationDrafting from "./components/ApplicationDrafting";
import PetitionDrafting from "./components/PetitionDrafting";
import ContractDrafting from "./components/PetitionDrafting/ContractDrafting";
import CaseManagmentSystem from "./pages/CaseManagmentSystem/index";
import CmsSharedDocumentPage from "./pages/CmsSharedDocumentPage";
import CaseManagement from "./pages/CaseManagement/index";
import "./index.css";
import EmailVerificationSuccessPage from "./pages/EmailVerificationSuccessPage";
import SubscriptionPlans from "./pages/Subscriptions";
import { theme } from "./config/muiTheme";
import { TutorialVideos } from "./pages/Tutorial Videos";
import { LegalChat } from "./pages/LegalChat";
import { CaseSearch } from "./pages/CaseSearch";
import { StatuteSearchParent } from "./pages/StatuteSearch";
import { DocumentAnalysisParent } from "./pages/DocumentAnalysis";
import { ErrorBoundaryWithKey } from "./components/ErrorBoundary";
import InvitationResponse from "./pages/CaseManagmentSystem/InvitationResponse";
import { JudgeGPTParent } from "./pages/JudgeGPT";
import ContractAnalysis from "./pages/ContractAnalysis/ContractAnalysis";
import ChatRoutePage from "./pages/RagChat/ChatRoutePage";
import ComingSoon from "./components/CaseManagement/Components/ComingSoon";
import { USER_INFO_KEY } from "./utils/HelperFunction/Constants";
import { setEncryptedItem } from "./utils/storageUtils";
const root = ReactDOM.createRoot(document.getElementById("root"));
initializeGA();

// Subscribe to Redux store for userInfo changes
store.subscribe(() => {
  const userLogin = store.getState().userLogin;
  const { userInfo } = userLogin;

  if (userInfo && userInfo._id) {
    // Reinitialize with the new user's ID
    initializeGA();
  } else {
    // Deinitialize when user logs out
    deinitializeGA();
  }
});
// msal instance
const msalInstance = new PublicClientApplication(msalConfig);
msalInstance.initialize();
const activeAccount = msalInstance.getActiveAccount();

if (!activeAccount) {
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length > 0) {
    msalInstance.setActiveAccount(accounts[0]);
  }
}

// Set the account on successful login
msalInstance.addEventCallback((event) => {
  if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
    const authenticationResult = event.payload;
    const account = authenticationResult.account;
    msalInstance.setActiveAccount(account);
  }
});

// Enable account storage events
msalInstance.enableAccountStorageEvents();

const TrackPageViews = ({ children }) => {
  const location = useLocation();
  const dispatch = useDispatch();

  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location]);

  useEffect(() => {
    const handleResponse = async (response) => {
      if (response && response.account) {
        const { name, username } = response.account;
        try {
          const res = await fetch(
            `https://rg-law-gpt-be-auth2.azurewebsites.net/api/createUserWithGoogle`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({ name, email: username }),
            },
          );
          const data = await res.json();
          setEncryptedItem(USER_INFO_KEY, data);
          dispatch({ type: "USER_LOGIN_SUCCESS", payload: data });
          <Navigate to="/app/chat" />;
        } catch (error) {
          console.log("Failed to register user:", error);
        }
      }
    };
    msalInstance.handleRedirectPromise().then(handleResponse);

    // Check if the user is already signed in
    const account = msalInstance.getActiveAccount();
    if (account) {
      // User is already signed in, navigate to the app
      handleResponse({ account });
    }
  }, [dispatch]);

  return children;
};

initializeGA();

// Disable browser DevTools access in production
if (process.env.REACT_APP_ENV === "production") {
  document.addEventListener("contextmenu", (e) => e.preventDefault());
  document.addEventListener("keydown", (e) => {
    if (
      e.key === "F12" ||
      (e.ctrlKey && e.shiftKey && ["I", "J", "C", "K"].includes(e.key.toUpperCase())) ||
      (e.ctrlKey && e.key.toUpperCase() === "U")
    ) {
      e.preventDefault();
    }
  });
}

window.addEventListener("error", (e) => {
  if (e.message === "ResizeObserver loop completed with undelivered notifications") {
    e.stopImmediatePropagation();
  }
});

root.render(
  <HelmetProvider>
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          <MsalProvider instance={msalInstance}>
            <GoogleOAuthProvider clientId="168527565999-ku8psofqjgsf673vh38ekf94rns19sv2.apps.googleusercontent.com">
              <React.StrictMode>
                <Router>
                  <TrackPageViews>
                    <ErrorBoundaryWithKey>
                      <Routes>
                        <Route path="/" element={<LoginPage />} />
                        <Route path="/signup" element={<SignUpForm />} />
                        <Route
                          path="/nadra-onboarding"
                          element={<NadraOnboardingForm />}
                        />
                        <Route
                          path="/forgot-password"
                          element={<ForgotPassword />}
                        />
                        <Route
                          path="/reset-password/:token"
                          element={<ResetPasswordPage />}
                        />
                        <Route
                          path="/verify-email/:token"
                          element={<EmailVerificationSuccessPage />}
                        />
                        <Route
                          path="join-as-a-lawyer/:token"
                          element={<InvitationResponse />}
                        />
                        {/* Protected routes */}
                        <Route path="/app" element={<App />}>
                          <Route index element={<Navigate to="legal-chat" />} />
                          <Route path="home" element={<HomePage />} />
                          <Route path="legal-chat" element={<LegalChat />} />
                          <Route
                            path="searchResults"
                            element={<CaseSearch />}
                          />
                          <Route
                            path="searchResults/:caseId"
                            element={<CaseSearch />}
                          />
                          <Route
                            path="application-drafting"
                            element={<ApplicationDrafting />}
                          />
                          <Route
                            path="application-drafting/:documentId"
                            element={<ApplicationDrafting />}
                          />
                          <Route
                            path="petition-drafting"
                            element={<PetitionDrafting />}
                          />
                          <Route
                            path="petition-drafting/:documentId"
                            element={<PetitionDrafting />}
                          />
                          <Route
                            path="contract-drafting"
                            element={<ContractDrafting />}
                          />
                          <Route
                            path="contract-drafting/:documentId"
                            element={<ContractDrafting />}
                          />

                          <Route
                            path="statute-search"
                            element={<StatuteSearchParent />}
                          />
                          <Route
                            path="statute-search/:statuteName"
                            element={<StatuteSearchParent />}
                          />
                          <Route path="bookmarks" element={<Bookmarks />} />
                          <Route
                            path="document-analysis"
                            element={<DocumentAnalysisParent />}
                          />
                          <Route
                            path="legal-library"
                            element={<LegalLibrary />}
                          />
                          {/* <Route
                            path="case-management-system"
                            element={<CaseManagmentSystem />}
                          /> */}
                          <Route
                            path="case-management"
                            element={<CaseManagmentSystem />}
                          />
                          <Route
                            path="cms-document/:caseId/:docId"
                            element={<CmsSharedDocumentPage />}
                          />
                          {/* <Route
                            path="judge-gpt"
                            element={<JudgeGPTParent />}
                          /> */}
                          <Route path="judge-gpt" element={<ComingSoon />} />
                          {/* <Route
                          path="subscriptions"
                          element={<SubscriptionPlans />}
                        /> */}
                          <Route
                            path="tutorial-videos"
                            element={<TutorialVideos />}
                          />
                          <Route
                            path="contract-analysis"
                            element={<ContractAnalysis />}
                          />
                          <Route
                            path="document-chat"
                            element={<ChatRoutePage />}
                          />
                          <Route
                            path="document-chat/:chatId"
                            element={<ChatRoutePage />}
                          />
                        </Route>
                        <Route path="*" element={<Navigate to="/" />} />
                      </Routes>
                    </ErrorBoundaryWithKey>
                  </TrackPageViews>
                </Router>
              </React.StrictMode>
            </GoogleOAuthProvider>
          </MsalProvider>
        </PersistGate>
      </Provider>
    </ThemeProvider>
  </HelmetProvider>,
);
